import React, { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton } from "@mui/material";
import AttachFileIcon from '@mui/icons-material/AttachFile';
import Delete from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import UploadFileIcon from '../../../assets/icons/iconUpload.svg';
import ImportTableCircleIcon from '../../../assets/icons/iconImportTableCircle.svg';
import MessageModal from './MessageModal';
import ExampleTable from '../../../assets/images/tableExample.png';


interface ImportExcelModalProps {
    isModalOpen: boolean;
    setIsModalOpen: (isOpen: boolean) => void;
    handleImportStudents: (file: File) => void;
}

export default function ImportExcelModal({ isModalOpen, setIsModalOpen, handleImportStudents }: ImportExcelModalProps) {
    const { t } = useTranslation();
    const [uploadedExcel, setUploadedExcel] = useState<File | null>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [isExampleModalOpen, setIsExampleModalOpen] = useState(false);

    const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            setUploadedExcel(file);
        }
    };

    const handleRemoveFile = () => {
        setUploadedExcel(null);
    };

    const handleSubmit = () => {
        if (uploadedExcel) {
            handleImportStudents(uploadedExcel);
            setUploadedExcel(null);
            setIsModalOpen(false);
        }
    };

    const handleDragEnter = useCallback((event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setIsDragging(true);
    }, []);

    const handleDragLeave = useCallback((event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setIsDragging(false);
    }, []);

    const handleDrop = useCallback((event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setIsDragging(false);
        const file = event.dataTransfer.files[0];
        if (
            file &&
            (file.type === "application/vnd.ms-excel" ||
                file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
                file.type === "text/csv")
        ) {
            setUploadedExcel(file);
        }
    }, []);

    const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
    };

    return (
        <>
            <Dialog className="modal-add-class" open={isModalOpen} onClose={() => setIsModalOpen(false)}>
                <IconButton
                    onClick={() => setIsModalOpen(false)}
                    className="close-button"
                    sx={{ color: '#666' }}
                >
                    <CloseIcon />
                </IconButton>
                <DialogTitle>{t('importStudents')}</DialogTitle>
                <img className="icon" src={ImportTableCircleIcon} alt="add-class-icon" />

                <DialogContent>
                    <div
                        className={`upload-file-area ${isDragging ? 'dragging' : ''}`}
                        onDrop={handleDrop}
                        onDragOver={handleDragOver}
                        onDragEnter={handleDragEnter}
                        onDragLeave={handleDragLeave}
                    >
                        <input
                            type="file"
                            hidden
                            id="file-upload"
                            onChange={handleFileUpload}
                            accept=".xlsx,.xls,.csv"
                        />
                        <div className="student-container">
                            <label htmlFor="file-upload" style={{ display: 'block', cursor: 'pointer', padding: '40px 40px 8px 40px', textAlign: 'center' }}>
                                {uploadedExcel ? (
                                    <>
                                        <AttachFileIcon sx={{ fontSize: '48px', color: '#B689E3' }} />
                                        <p>{uploadedExcel.name}</p>
                                        <IconButton
                                            onClick={(e) => {
                                                e.preventDefault();
                                                handleRemoveFile();
                                            }}
                                            sx={{
                                                position: 'absolute',
                                                top: '8px',
                                                insetInlineEnd: '8px',
                                            }}
                                        >
                                            <Delete sx={{
                                                height: '32px',
                                                width: '32px',
                                                color: '#e9dbf7',
                                                margin: '2px',
                                                '&:hover': {
                                                    color: '#B689E3'
                                                }
                                            }} />
                                        </IconButton>
                                    </>
                                ) : (
                                    <>
                                        <img src={UploadFileIcon} alt="upload-file-icon" />
                                        <p>{t('dragAndDropOrClick')}</p>
                                        <Button
                                            variant="text"
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                setIsExampleModalOpen(true);
                                            }}
                                        >
                                            {t('tableExamle')}
                                        </Button>
                                    </>
                                )}
                            </label>
                        </div>
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button
                        className="cancel-button circle"
                        onClick={() => setIsModalOpen(false)}
                        variant="outlined"
                    >
                        {t('cancel')}
                    </Button>
                    <Button
                        className="import-button circle"
                        onClick={handleSubmit}
                        variant="contained"
                        disabled={!uploadedExcel}
                    >
                        {t('import')}
                    </Button>
                </DialogActions>
            </Dialog>

            <MessageModal
                isOpen={isExampleModalOpen}
                onClose={() => setIsExampleModalOpen(false)}
                title={t('excelStructure', 'מבנה קובץ האקסל')}
                type="info"
                message={
                    <div style={{ textAlign: 'center' }}>
                        <p style={{ textAlign: 'center', fontWeight: '200' }}>
                            {t('uploadExcelInstructions')}
                            <br />
                            <ol style={{ paddingInlineStart: '20%', listStylePosition: 'inside', listStyleType: 'decimal', textAlign: 'justify' }}>
                                <li>{t('studentNameColumn')}</li>
                                <li>{t('studentEmailColumn')}</li>
                            </ol>
                        </p>
                        <img
                            src={ExampleTable}
                            alt={t('excelTableExample')}
                            style={{
                                maxWidth: '300px',
                                margin: '16px 0',
                                border: '3px solid #b689e333',
                                borderRadius: '12px'
                            }}
                        />
                    </div>
                }

            />
        </>
    );
}
